import React from 'react';

// 🎯 [교정] 부모 및 테이블들과 상태를 공유하기 위해 viewMode 프롭을 주입받습니다.
const AnnualFinancialTrendChart = ({ trendData, viewMode = 'actual' }) => {
    
    // 🎯 [추가] viewMode가 forecast일 때는 forecastCurrent를, actual일 때는 current를 타겟으로 지정
    const activeCurrentData = viewMode === 'forecast' ? trendData?.forecastCurrent : trendData?.current;

    // 💡 안전하게 데이터를 읽어오기 위한 구조 분해 할당 및 기본값 설정 (가변 데이터 대응)
    const current = activeCurrentData || { sales: 0, buy: 0, profit: 0, margin: "0.0" };
    const past = trendData?.past || { sales: 0, buy: 0, profit: 0, margin: "0.0" };
    
    // 🎯 [교정] 포캐스팅 모드일 때는 타이틀에 '예측' 명시
    const currentYear = trendData?.currentYear 
        ? (viewMode === 'forecast' ? `${trendData.currentYear}년` : `${trendData.currentYear}년`) 
        : (viewMode === 'forecast' ? "올해 예측" : "올해");
    const pastYear = trendData?.pastYear ? `${trendData.pastYear}년` : "작년";

    // 🎯 [교정] 포캐스팅 모드(past 가려짐)일 때는 오직 올해 예측치의 매출 최댓값만 추적하여 비율 연산 오차 방지
    const maxSalesValue = viewMode === 'forecast'
        ? (Number(current.sales || 0) || 1)
        : (Math.max(Number(current.sales || 0), Number(past.sales || 0)) || 1); 

    // 하드코딩되었던 배열을 전달받은 동적 데이터 구조로 리맵핑
    const renderedTrendData = [
        {
            year: currentYear,
            visible: true, // 🎯 [추가] 렌더링 노출 여부 플래그
            metrics: [
                { label: "매출액", value: Number(current.sales), displayValue: `₩ ${current.sales.toLocaleString()}`, marginValue: "", barColor: "#0284c7" }, 
                { label: "매입액", value: Number(current.buy), displayValue: `₩ ${current.buy.toLocaleString()}`, marginValue: "", barColor: "#ea580c" }, 
                { label: "이익액", value: Number(current.profit), displayValue: `₩ ${current.profit.toLocaleString()}`, marginValue: `${current.margin}%`, barColor: "#16a34a", textColor: "#16a34a" } 
            ]
        },
        {
            year: pastYear,
            visible: viewMode === 'actual', // 🎯 [추가] 포캐스팅 모드('forecast')일 때는 past 데이터를 가려버림(false)
            metrics: [
                { label: "매출액", value: Number(past.sales), displayValue: `₩ ${past.sales.toLocaleString()}`, marginValue: "", barColor: "#0284c7" },
                { label: "매입액", value: Number(past.buy), displayValue: `₩ ${past.buy.toLocaleString()}`, marginValue: "", barColor: "#ea580c" },
                { label: "이익액", value: Number(past.profit), displayValue: `₩ ${past.profit.toLocaleString()}`, marginValue: `${past.margin}%`, barColor: "#16a34a", textColor: "#16a34a" }
            ]
        }
    ];

    // 🎯 visible 플래그가 true인 항목만 필터링하여 데이터 목록 확정
    const filteredTrendData = renderedTrendData.filter(section => section.visible);
    return (
        <div style={{ width: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            
            {/* 컴포넌트 타이틀 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <h2 style={{ margin: 0, fontWeight: '700', fontSize: '14px', color: '#1f2937' }}>
                    {/* 🎯 [텍스트 교정] 포캐스팅 상태에 맞춰 컴포넌트 타이틀 유기적 변환 */}
                    {viewMode === 'actual' ? '연도별 성과 추이' : `${currentYear} 포캐스팅 추이`}
                </h2>
            </div>

            {/* 🎯 [필터링 루프] filteredTrendData를 바탕으로 돌기 때문에 forecast 모드일 때는 past 항목이 자동으로 가려집니다. */}
            {filteredTrendData.map((section, sIdx) => (
                <div key={sIdx} style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '5px',
                    paddingBottom: sIdx === filteredTrendData.length - 1 ? '0px' : '16px',
                    borderBottom: sIdx === filteredTrendData.length - 1 ? 'none' : '1px dashed #e5e7eb'
                }}>
                    {section.metrics.map((metric, mIdx) => {
                        // 최대 가로 너비 기준 비율 계산 (0% ~ 100%)
                        const fillPercentage = (metric.value / maxSalesValue) * 100;

                        return (
                            <div key={mIdx} style={{ 
                                display: 'flex', 
                                flexDirection: 'row', 
                                alignItems: 'center', 
                                fontSize: '11px',
                                height: '18px'
                            }}>
                                {/* 1. 맨 좌측 연도 표현 (첫 행에만 표기) */}
                                <span style={{ 
                                    width: '85px', /* 🎯 '2026년 예측' 등 늘어난 텍스트 규격 소화 및 간격 유지를 위해 너비 55px -> 85px로 확장 */
                                    fontWeight: '700', 
                                    color: '#1f2937',
                                    fontSize: '12px'
                                }}>
                                    {mIdx === 0 ? section.year : ''}
                                </span>

                                {/* 2. 중간 구분 라벨명 */}
                                <span style={{ 
                                    width: '55px', 
                                    color: metric.textColor || '#6b7280', 
                                    fontWeight: metric.textColor ? '700' : '500'
                                }}>
                                    {metric.label}
                                </span>

                                {/* 3. 중앙 연회색 가이드 트랙 바 배경 */}
                                <div style={{ 
                                    flex: 1, 
                                    backgroundColor: '#f1f5f9', 
                                    height: '12px', 
                                    borderRadius: '2px',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    marginRight: '16px'
                                }}>
                                    <div style={{ 
                                        width: `${fillPercentage}%`, 
                                        backgroundColor: metric.barColor, 
                                        height: '100%', 
                                        borderRadius: '2px',
                                        transition: 'width 0.4s ease-in-out'
                                    }} />
                                </div>

                                {/* 4. 💸 우측 금액 열 */}
                                <div style={{ 
                                    width: '130px', 
                                    textAlign: 'right', 
                                    fontSize: '13px', 
                                    fontWeight: '700', 
                                    color: metric.textColor || '#374151',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {metric.displayValue}
                                </div>

                                {/* 5. 📊 이익률 독립 단독 열 (매출이익 행에만 노출) */}
                                <div style={{ 
                                    width: '95px', 
                                    textAlign: 'right', 
                                    fontSize: '12px',
                                    fontWeight: '700', 
                                    color: '#16a34a',
                                    whiteSpace: 'nowrap'
                                }}>
                                    {metric.marginValue && metric.value > 0 ? `(이익률 ${metric.marginValue})` : ''}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

export default AnnualFinancialTrendChart;
